import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type AppearanceMode = "light" | "dark" | "auto";

const getInitialAppearance = (): AppearanceMode => {
  const saved = localStorage.getItem("appearanceMode");
  if (saved && ["light", "dark", "auto"].includes(saved)) {
    return saved as AppearanceMode;
  }
  return "dark";
};

const getInitialDarkMode = (appearance: AppearanceMode): boolean => {
  if (appearance === "auto") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  return appearance === "dark";
};

const updateDocumentClass = (isDark: boolean) => {
  document?.body?.classList?.add("theme-transitioning");

  if (isDark) {
    document?.body?.classList?.add("dark");
  } else {
    document?.body?.classList?.remove("dark");
  }

  setTimeout(() => {
    document?.body?.classList?.remove("theme-transitioning");
  }, 300);
};

const initialAppearance = getInitialAppearance();
const initialDarkMode = getInitialDarkMode(initialAppearance);

interface ThemeState {
  isDarkMode: boolean;
  appearanceMode: AppearanceMode;
}

export const themeProviderSlice = createSlice({
  name: "themeProvider",
  initialState: {
    isDarkMode: initialDarkMode,
    appearanceMode: initialAppearance,
  } as ThemeState,
  reducers: {
    setAppearanceMode: (state, action: PayloadAction<AppearanceMode>) => {
      state.appearanceMode = action.payload;
      localStorage.setItem("appearanceMode", action.payload);

      let newDarkMode: boolean;
      if (action.payload === "auto") {
        newDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
      } else {
        newDarkMode = action.payload === "dark";
      }

      state.isDarkMode = newDarkMode;
      localStorage.setItem("isDarkMode", JSON.stringify(newDarkMode));
      updateDocumentClass(newDarkMode);
    },
  },
});

updateDocumentClass(initialDarkMode);

export const { setAppearanceMode } = themeProviderSlice.actions;

  